import React from 'react'
import PropTypes from 'prop-types'
import CoachesSearchFindResultsState from '@layouts/coaches-search/CoachesSearchFindResultsState'
import { request } from 'graphql-request'
import isEqual from 'lodash/isEqual'
import Router from 'next/router'
import { omit, path as Rpath } from 'ramda'
import { findResultsState } from 'react-instantsearch-dom/server'
import { isServer } from '@utils'
import {
  getBookThisWeekObjectIds,
  isBookThisWeekParamEnabled,
} from '@lib/coaches-directory/book-this-week'
import {
  updateAfterMilliseconds,
  createURL,
  searchClient,
  pathToSearchState,
  searchStateToURL,
  DEFAULT_PROPS,
  omitDirectoryConfigureFilters,
} from './utils'
import { swapPersonaPreset } from './PopularGoals/persona-refinement-presets'
import { clearDirectoryRefinements } from './Refiners/refiner-filter-bundles'

const REFINERS_QUERY = `{
    Services {
      serviceTypes {
        typeName
        services {
          serviceName
        }
      }
    }
    Experiences {
      experienceTypes {
        typeName
        experiences {
          experienceName
        }
      }
    }
  }`

// Persona and Book this week live on the URL but are NOT InstantSearch
// searchState — InstantSearch ignores (and on internal syncs, strips)
// top-level keys it does not own. Tracking them separately keeps the
// selection sticky even as the user types, refines, or paginates.
const splitDirectoryUiState = (state) => {
  if (!state || typeof state !== 'object') {
    return { persona: null, bookThisWeek: false, searchState: state || {} }
  }
  const { persona, bookThisWeek, ...searchState } = state
  return {
    persona: persona || null,
    bookThisWeek: isBookThisWeekParamEnabled(bookThisWeek),
    searchState,
  }
}

const urlStateFromDirectory = ({
  searchState,
  persona,
  placeName,
  bookThisWeek,
}) => ({
  ...searchState,
  ...(persona && { persona }),
  ...(placeName && { placeName }),
  ...(bookThisWeek && { bookThisWeek: true }),
})

const withSearchComp = (Page) => {
  class CoachSearchResults extends React.Component {
    constructor(props) {
      super(props)
      const { searchState: initial, router } = props
      const { persona, bookThisWeek, searchState } = splitDirectoryUiState(initial)
      this.state = {
        persona,
        bookThisWeek,
        searchState,
        lastRouter: router,
        aroundLatLng: searchState.aroundLatLng,
        aroundRadius: searchState.aroundRadius || null,
        placeName: searchState.placeName || null,
      }
    }

    static async getInitialProps(props) {
      const { asPath, query } = props

      // Refiners feed both the UI and the persona preset expansion in
      // pathToSearchState, so fetch them before resolving searchState.
      let refinerData
      try {
        refinerData = await request(process.env.COACH_TOOLS_GRAPHQL_ENDPOINT, REFINERS_QUERY)
      } catch (e) {
        refinerData = {}
      }

      const searchState = await pathToSearchState(asPath, query.param, refinerData)
      const bookThisWeekOnly = isBookThisWeekParamEnabled(searchState.bookThisWeek)
      let bookThisWeekObjectIDs
      // SSR via-ip is the host, not the visitor. Skip the join until the URL
      // already has aroundLatLng; the client resolves IDs after IP hydrate.
      if (bookThisWeekOnly && searchState.aroundLatLng) {
        try {
          bookThisWeekObjectIDs = await getBookThisWeekObjectIds({
            aroundLatLng: searchState.aroundLatLng,
            aroundRadius: searchState.aroundRadius,
          })
        } catch {
          bookThisWeekObjectIDs = undefined
        }
      }

      let resultsState = null
      if (isServer()) {
        // pre-hydrate search results when server rendering
        resultsState = await findResultsState(CoachesSearchFindResultsState, {
          ...DEFAULT_PROPS,
          searchState,
          aroundLatLng: searchState.aroundLatLng,
          aroundRadius: searchState.aroundRadius,
          bookThisWeekOnly,
          bookThisWeekObjectIDs,
        })
      }
      return {
        resultsState,
        searchState,
        refinerData,
        bookThisWeekObjectIDs,
      }
    }

    static async getDerivedStateFromProps(props, state) {
      if (!isEqual(state.lastRouter, props.router)) {
        const next = await pathToSearchState(
          props.router.asPath,
          Rpath(['router', 'query', 'param'], props),
          props.refinerData,
        )
        const { persona, bookThisWeek, searchState } = splitDirectoryUiState(next)
        return {
          persona,
          bookThisWeek,
          searchState,
          lastRouter: props.router,
          aroundLatLng: searchState.aroundLatLng,
          aroundRadius: searchState.aroundRadius || null,
          placeName: searchState.placeName || null,
        }
      }

      return null
    }

    scheduleUrlSync = (urlSearchState) => {
      const { aroundLatLng, aroundRadius } = this.state
      const { refinerData } = this.props
      clearTimeout(this.debouncedSetState)
      this.debouncedSetState = setTimeout(async () => {
        const href = await searchStateToURL(urlSearchState, aroundLatLng, aroundRadius, refinerData)
        Router.push(window.location.pathname, href, {
          shallow: true,
        })
      }, updateAfterMilliseconds)
    }

    onSearchStateChange = (instantSearchState) => {
      const {
        persona, placeName, bookThisWeek, searchState,
      } = this.state
      const nextSearchState = omitDirectoryConfigureFilters(instantSearchState)
      // <Configure> (mount, IP pin) makes InstantSearch report a state change
      // that differs only by `configure`. That is not a user edit, so keep it
      // out of the URL; otherwise /coaches/search rewrites itself.
      if (isEqual(omit(['configure'], nextSearchState), omit(['configure'], searchState))) {
        this.setState({ searchState: nextSearchState })
        return
      }
      this.scheduleUrlSync(urlStateFromDirectory({
        searchState: nextSearchState,
        persona,
        placeName,
        bookThisWeek,
      }))
      this.setState({ searchState: nextSearchState })
    }

    onPersonaChange = (nextPersonaId) => {
      const {
        searchState, persona, placeName, bookThisWeek,
      } = this.state
      const { refinerData } = this.props

      const merged = swapPersonaPreset(
        { ...searchState, ...(persona && { persona }) },
        nextPersonaId,
        refinerData,
      )
      const { persona: newPersona, searchState: nextSearchState } = splitDirectoryUiState(merged)

      this.scheduleUrlSync(urlStateFromDirectory({
        searchState: nextSearchState,
        persona: newPersona,
        placeName,
        bookThisWeek,
      }))
      this.setState({ persona: newPersona, searchState: nextSearchState })
    }

    onToggleBookThisWeek = () => {
      const {
        searchState, persona, placeName, bookThisWeek,
      } = this.state
      const nextBookThisWeek = !bookThisWeek
      this.scheduleUrlSync(urlStateFromDirectory({
        searchState,
        persona,
        placeName,
        bookThisWeek: nextBookThisWeek,
      }))
      this.setState({ bookThisWeek: nextBookThisWeek })
    }

    onClearDirectoryFilters = () => {
      const { searchState, placeName } = this.state
      const nextSearchState = clearDirectoryRefinements(searchState)
      this.scheduleUrlSync(urlStateFromDirectory({
        searchState: nextSearchState,
        persona: null,
        placeName,
        bookThisWeek: false,
      }))
      this.setState({
        persona: null,
        bookThisWeek: false,
        searchState: nextSearchState,
      })
    }

    syncUrlFromState = (instantSearchState) => {
      const { persona, placeName, bookThisWeek } = this.state
      this.scheduleUrlSync(urlStateFromDirectory({
        searchState: omitDirectoryConfigureFilters(instantSearchState),
        persona,
        placeName,
        bookThisWeek,
      }))
    }

    setAroundLatLng = (locationStr, nextPlaceName, { syncUrl = true } = {}) => {
      const { searchState } = this.state
      this.setState({
        aroundLatLng: locationStr,
        ...(nextPlaceName ? { placeName: nextPlaceName } : {}),
      }, () => {
        // Auto-detected (IP) locations stay out of the URL so /coaches/search
        // does not rewrite itself for visitors and crawlers.
        if (syncUrl) this.syncUrlFromState(searchState)
      })
    }

    setAroundRadius = (radiusStr) => {
      const { searchState } = this.state
      this.setState({
        aroundRadius: radiusStr,
      }, () => this.syncUrlFromState(searchState))
    }

    render() {
      const {
        searchState, persona, bookThisWeek, aroundLatLng, aroundRadius, placeName,
      } = this.state
      const { resultsState, refinerData, bookThisWeekObjectIDs } = this.props
      return (
        <Page
          searchClient={searchClient}
          {...DEFAULT_PROPS}
          searchState={searchState}
          persona={persona}
          bookThisWeek={bookThisWeek}
          bookThisWeekObjectIDs={bookThisWeekObjectIDs}
          resultsState={resultsState}
          onSearchStateChange={this.onSearchStateChange}
          onPersonaChange={this.onPersonaChange}
          onToggleBookThisWeek={this.onToggleBookThisWeek}
          onClearDirectoryFilters={this.onClearDirectoryFilters}
          createURL={createURL}
          aroundLatLng={aroundLatLng}
          aroundRadius={aroundRadius}
          setAroundLatLng={this.setAroundLatLng}
          setAroundRadius={this.setAroundRadius}
          placeName={placeName}
          refinerData={refinerData}
        />
      )
    }
  }

  CoachSearchResults.propTypes = {
    searchState: PropTypes.object,
    router: PropTypes.any,
    resultsState: PropTypes.object,
    bookThisWeekObjectIDs: PropTypes.arrayOf(PropTypes.string),
    refinerData: PropTypes.shape({
      Services: PropTypes.shape({
        serviceTypes: PropTypes.arrayOf(PropTypes.shape({
          typeName: PropTypes.string,
          services: PropTypes.arrayOf(PropTypes.shape({
            serviceName: PropTypes.string,
          })),
        })),
      }),
      Experiences: PropTypes.shape({
        experienceTypes: PropTypes.arrayOf(PropTypes.shape({
          typeName: PropTypes.string,
          experiences: PropTypes.arrayOf(PropTypes.shape({
            experienceName: PropTypes.string,
          })),
        })),
      }),
    }),
  }

  return CoachSearchResults
}

export default withSearchComp
